import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import React, { useMemo } from 'react';
import Select from 'react-select';
import { BrandSwitch } from '../../../components/BrandSwitch';
import { Button } from '../../../components/button';
import { Fieldset, Field, Label } from '../../../components/fieldset';
import { Input, InputGroup } from '../../../components/input';
import { useCodeDefinitionsAsOptions } from '../../../hooks/useCodeDefinitions';
import { useWorkflowMetadata } from '../../../hooks/useWorkflowMetadata';
import { reactSelectReset } from '../../../utils/utils';

const sortByOptions = [
  { value: 'title', label: 'Need Name' },
  { value: 'facility__name', label: 'Facility Name' },
  { value: 'facility__state', label: 'Facility State' },
  { value: 'specialty', label: 'Specialty' },
  { value: 'current_status__title', label: 'Status' },
  // { value: 'urgency_level', label: 'Urgency Level' },
  { value: 'target_on', label: 'Target Start Date' },
];

const sortDirectionOptions = [
  { value: '', label: 'Ascending' },
  { value: '-', label: 'Descending' },
];

interface NeedFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  sortBy: string;
  onSortByChange: (value: string) => void;
  sortDirection: string;
  onSortDirectionChange: (value: string) => void;
  showClosed: boolean;
  onShowClosedChange: (value: boolean) => void;
  justification: string;
  onJustificationChange: (value: string) => void;
  urgencyLevel: string;
  onUrgencyLevelChange: (value: string) => void;
  currentStatus: string;
  onCurrentStatusChange: (value: string) => void;
  currentReason: string;
  onCurrentReasonChange: (value: string) => void;
  onReset: () => void;
}

const NeedFilters: React.FC<NeedFiltersProps> = ({
  search,
  onSearchChange,
  sortBy,
  onSortByChange,
  sortDirection,
  onSortDirectionChange,
  showClosed,
  onShowClosedChange,
  justification,
  onJustificationChange,
  urgencyLevel,
  onUrgencyLevelChange,
  currentStatus,
  onCurrentStatusChange,
  currentReason,
  onCurrentReasonChange,
  onReset,
}) => {
  const { data: justificationOptionsData } = useCodeDefinitionsAsOptions('JustificationType');
  const { data: urgencyLevelOptionsData } = useCodeDefinitionsAsOptions('UrgencyLevel');
  const { statuses } = useWorkflowMetadata('staffing.need');

  // no obvious default, so temporarily add "all" option
  const justificationOptions = useMemo(
    () => [{ value: '', label: 'All Justifications' }, ...(justificationOptionsData || [])],
    [justificationOptionsData]
  );
  // no obvious default, so temporarily add "all" option
  const urgencyLevelOptions = useMemo(
    () => [{ value: '', label: 'All Urgency Levels' }, ...(urgencyLevelOptionsData || [])],
    [urgencyLevelOptionsData]
  );

  // no obvious default, so temporarily add "all" option
  const statusOptions = useMemo(() => {
    const options = Object.values(statuses).map((status) => ({
      value: status.code,
      label: status.title,
    }));
    return [{ value: '', label: 'All Statuses' }, ...options];
  }, [statuses]);

  // no obvious default, so temporarily add "all" option
  const reasonOptions = useMemo(() => {
    const reasonsMap = new Map<string, string>();
    Object.values(statuses).forEach((status) => {
      status.reasons.forEach((reason) => {
        reasonsMap.set(reason.code, reason.title);
      });
    });
    const options = Array.from(reasonsMap.entries()).map(([code, title]) => ({
      value: code,
      label: title,
    }));
    return [{ value: '', label: 'All Reasons' }, ...options];
  }, [statuses]);
  return (
    <div
      id="search-filters-section"
      className="tw:bg-white tw:rounded-lg tw:shadow-sm tw:border tw:border-zinc-200 tw:py-3 tw:px-4 tw:dark:bg-zinc-700 tw:dark:border-zinc-400"
    >
      <Fieldset className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:lg:grid-cols-3 tw:gap-4 tw:mb-4">
        {/* Search Box */}
        <Field>
          <Label htmlFor="search-input">Search Needs</Label>
          <InputGroup>
            <MagnifyingGlassIcon data-slot="icon" />
            <Input
              id="search-input"
              type="text"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </InputGroup>
        </Field>
        {/* Sort By */}
        {/* <Field>
          <Label htmlFor="sort-by-select">Sort By</Label>
          <Select
            id="sort-by-select"
            options={sortByOptions}
            value={sortByOptions.find((opt) => opt.value === sortBy) || null}
            onChange={(option) => onSortByChange(option?.value || '')}
            placeholder="Select sort field"
            className="tw:w-full"
            styles={reactSelectReset}
          />
        </Field> */}
        {/* Sort Direction */}
        {/* <Field>
          <Label htmlFor="sort-direction-select">Sort Direction</Label>
          <Select
            id="sort-direction-select"
            options={sortDirectionOptions}
            value={sortDirectionOptions.find((opt) => opt.value === sortDirection) || null}
            onChange={(option) => onSortDirectionChange(option?.value || '')}
            placeholder="Select direction"
            className="tw:w-full"
            styles={reactSelectReset}
          />
        </Field> */}
        {/* Urgency Level Filter */}
        <Field>
          <Label htmlFor="urgency-level-select">Urgency Level</Label>
          <Select
            id="urgency-level-select"
            options={urgencyLevelOptions}
            value={urgencyLevelOptions.find((opt) => opt.value === urgencyLevel) || null}
            onChange={(option) => onUrgencyLevelChange(option?.value || '')}
            placeholder="Filter by urgency"
            className="tw:w-full"
            styles={reactSelectReset}
            isClearable
          />
        </Field>
        {/* Status Filter */}
        <Field>
          <Label htmlFor="status-select">Status</Label>
          <Select
            id="status-select"
            options={statusOptions}
            value={statusOptions.find((opt) => opt.value === currentStatus) || null}
            onChange={(option) => onCurrentStatusChange(option?.value || '')}
            placeholder="Filter by status"
            className="tw:w-full"
            styles={reactSelectReset}
            isClearable
          />
        </Field>
        {/* Reason Filter */}
        {/* <Field>
          <Label htmlFor="reason-select">Reason</Label>
          <Select
            id="reason-select"
            options={reasonOptions}
            value={reasonOptions.find((opt) => opt.value === currentReason) || null}
            onChange={(option) => onCurrentReasonChange(option?.value || '')}
            placeholder="Filter by reason"
            className="tw:w-full"
            styles={reactSelectReset}
            isClearable
          />
        </Field> */}
        {/* Justification Filter */}
        {/* <Field>
          <Label htmlFor="justification-select">Justification</Label>
          <Select
            id="justification-select"
            options={justificationOptions}
            value={justificationOptions.find((opt) => opt.value === justification) || null}
            onChange={(option) => onJustificationChange(option?.value || '')}
            placeholder="Filter by justification"
            className="tw:w-full"
            styles={reactSelectReset}
            isClearable
          />
        </Field> */}
      </Fieldset>
      {/* Show Closed Toggle and Reset Button */}
      <div className="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:w-full tw:mt-4">
        <Field className="tw:flex tw:items-center tw:gap-2">
          <BrandSwitch checked={showClosed} onChange={onShowClosedChange} id="show-closed-switch" />
          <Label htmlFor="show-closed-switch">Show Closed</Label>
        </Field>
        <Button color="zinc-outline" onClick={onReset}>
          Reset List
        </Button>
      </div>
    </div>
  );
};

export default NeedFilters;
